import { appEvents, AppEvent } from '../events/eventEmitter.js'
import { FieldValue } from 'firebase-admin/firestore'
import type { Firestore } from 'firebase-admin/firestore'
import type { RedisClient } from '../redis/client.js'
import { GamificationService } from '../../modules/gamification/gamification.service.js'
import { UsersRepository } from '../../modules/users/users.repository.js'
import { UsersService } from '../../modules/users/users.service.js'
import type { UserGamificationStats } from '@my-roadtrip/types'
import { createLogger } from '../../common/utils/logger.js'

class PlayerStatsUpdater {
  private readonly logger = createLogger('PlayerStatsUpdater')

  constructor(
    private readonly firestore: Firestore,
    private readonly usersService: UsersService
  ) {}

  async increment(userId: string, delta: Partial<UserGamificationStats>): Promise<void> {
    const payload = this.buildIncrementPayload(delta)
    if (!Object.keys(payload).length) {
      return
    }

    try {
      await this.firestore.collection('users').doc(userId).set(payload, { merge: true })
      await this.usersService.invalidateUserStatsCache(userId)
    } catch (error) {
      this.logger.warn('Failed to persist gamification stats increment', {
        userId,
        error: (error as Error).message
      })
      throw error
    }
  }

  private buildIncrementPayload(
    delta: Partial<UserGamificationStats>
  ): Record<string, ReturnType<typeof FieldValue.increment>> {
    const payload: Record<string, ReturnType<typeof FieldValue.increment>> = {}

    for (const [key, value] of Object.entries(delta)) {
      const numeric = Number(value)
      if (!Number.isFinite(numeric) || numeric === 0) {
        continue
      }
      payload[`gamification.stats.${key}`] = FieldValue.increment(numeric)
    }

    return payload
  }
}

/**
 * Gamification Event Listeners
 * 
 * Automatically awards points and unlocks badges based on user actions
 */
export function registerGamificationListeners(firestore: Firestore, redis: RedisClient) {
  const gamification = new GamificationService(firestore, redis)
  const usersRepository = new UsersRepository(firestore)
  const usersService = new UsersService(usersRepository, redis)
  const statsUpdater = new PlayerStatsUpdater(firestore, usersService)
  const logger = createLogger('GamificationListeners')
  
  // Plan created → Award points
  appEvents.onEvent(AppEvent.PLAN_CREATED, async (payload: any) => {
    const { userId } = payload
    if (!userId) return
    
    try {
      // Award 50 points for creating a plan
      await gamification.awardPoints(userId, 'plan_created', 50)
      await statsUpdater.increment(userId, { plansCreated: 1 })
      
      // Check if first plan (for badge)
      const profile = await gamification.getUserProfile(userId)
      if (profile?.gamification?.stats.plansCreated === 1) {
        // Auto-claim first_plan badge
        try {
          await gamification.claimBadge(userId, 'first_plan')
        } catch (err) {
          logger.debug('Badge already claimed or requirements not met', { badge: 'first_plan', userId, error: (err as Error).message })
        }
      }
    } catch (error) {
      logger.error('Failed to award points for plan creation', { userId, error: (error as Error).message })
    }
  })
  
  // Stage added → Award points
  appEvents.onEvent(AppEvent.STAGE_ADDED, async (payload: any) => {
    const { userId } = payload
    if (!userId) return
    
    try {
      // Award 10 points for adding a stage
      await gamification.awardPoints(userId, 'stage_added', 10)
      await statsUpdater.increment(userId, { stagesCreated: 1 })
    } catch (error) {
      logger.error('Failed to award points for stage addition', { userId, error: (error as Error).message })
    }
  })
  
  // Plan shared → Award points
  appEvents.onEvent(AppEvent.PLAN_SHARED, async (payload: any) => {
    const { ownerId } = payload
    if (!ownerId) return
    
    try {
      // Award 25 points for sharing a plan
      await gamification.awardPoints(ownerId, 'plan_shared', 25)
      await statsUpdater.increment(ownerId, { sharedPlans: 1 })
      
      // Check for socialite badge (5 shares)
      const profile = await gamification.getUserProfile(ownerId)
      if (profile?.gamification?.stats?.sharedPlans && profile.gamification.stats.sharedPlans >= 5) {
        try {
          await gamification.claimBadge(ownerId, 'socialite')
        } catch (err) {
          logger.debug('Badge already claimed or requirements not met', { badge: 'socialite', userId: ownerId, error: (err as Error).message })
        }
      }
    } catch (error) {
      logger.error('Failed to award points for plan sharing', { ownerId, error: (error as Error).message })
    }
  })

  // Plan share removed → Adjust stats
  appEvents.onEvent(AppEvent.PLAN_SHARE_REMOVED, async (payload: any) => {
    const { ownerId } = payload
    if (!ownerId) return

    try {
      await gamification.awardPoints(ownerId, 'plan_share_removed', -25)
      await statsUpdater.increment(ownerId, { sharedPlans: -1 })
    } catch (error) {
      logger.error('Failed to update stats for share removal', { ownerId, error: (error as Error).message })
    }
  })

  // Stage removed → Adjust stats/points
  appEvents.onEvent(AppEvent.STAGE_REMOVED, async (payload: any) => {
    const { userId } = payload
    if (!userId) return

    try {
      await gamification.awardPoints(userId, 'stage_removed', -10)
      await statsUpdater.increment(userId, { stagesCreated: -1 })
    } catch (error) {
      logger.error('Failed to update stats for stage removal', { userId, error: (error as Error).message })
    }
  })
  
  // Collaborator added → Award points
  appEvents.onEvent(AppEvent.COLLABORATOR_ADDED, async (payload: any) => {
    const { addedById, collaboratorId } = payload
    if (!addedById) return
    
    try {
      // Award 20 points to the person who invited
      await gamification.awardPoints(addedById, 'collaborator_added', 20)
      await statsUpdater.increment(addedById, { collaborations: 1 })
      
      // Award 10 points to the new collaborator
      if (collaboratorId) {
        await gamification.awardPoints(collaboratorId, 'joined_collaboration', 10)
        await statsUpdater.increment(collaboratorId, { collaborations: 1 })
        
        // Check for collaborator badge
        const profile = await gamification.getUserProfile(collaboratorId)
        if (profile?.gamification?.stats?.collaborations && profile.gamification.stats.collaborations >= 1) {
          try {
            await gamification.claimBadge(collaboratorId, 'collaborator')
          } catch (err) {
            logger.debug('Badge already claimed or requirements not met', { badge: 'collaborator', userId: collaboratorId, error: (err as Error).message })
          }
        }
      }
    } catch (error) {
      logger.error('Failed to award points for collaboration', { addedById, collaboratorId, error: (error as Error).message })
    }
  })
  
  // Comment added → Award points
  appEvents.onEvent(AppEvent.COMMENT_ADDED, async (payload: any) => {
    const { authorId } = payload
    if (!authorId) return
    
    try {
      // Award 5 points for adding a comment
      await gamification.awardPoints(authorId, 'comment_added', 5)
    } catch (error) {
      logger.error('Failed to award points for comment', { authorId, error: (error as Error).message })
    }
  })
  
  logger.info('Gamification event listeners registered')
}
